/* eslint-disable react-hooks/exhaustive-deps, react/prop-types */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { jwtDecode } from "jwt-decode";
import { useNavigate, Link } from 'react-router-dom';
import Pagination from "react-js-pagination";
import Navbar from "../Navbar";
import Header from "../Header";
import baseUrl from "../BaseUrl";
import { formatUTCDate } from "../../utils/utils";
import ExportButton from "../active-users/ExportButton";
import EmptyState from "../active-users/states/EmptyState";
import ErrorState from "../active-users/states/ErrorState";
import TableSkeleton from "../active-users/states/TableSkeleton";
import "../active-users/active-users.css";
import "./ContinuousUsageStreaks.css";

const LIMIT = 20;
const STATE_KEY = "continuousUsageStreaksState";

// Dropdown option ranges (FR-004 / FR-005).
const GAP_OPTIONS = Array.from({ length: 20 }, (_, i) => i + 1);      // 1..20
const DAYS_OPTIONS = Array.from({ length: 90 }, (_, i) => i + 1);     // 1..90
const DEFAULT_GAP = 4;
const DEFAULT_MIN_DAYS = 42;

// Qualifying-day mode (047). Changes ONLY how a day qualifies; gap/min are unchanged.
const DEFAULT_MODE = 'pause_alerts';
const MODE_OPTIONS = [
  { value: 'pause_alerts', label: 'Pause + Alerts' },
  { value: 'only_pause', label: 'Only Pause' },
  { value: 'only_alerts', label: 'Only Alerts' },
];
// Copy that names the active mode's rule (empty state + scope note).
const MODE_RULE = {
  pause_alerts: 'both generated a heart-rate alert and took at least one pause (meditation or breath)',
  only_pause: 'took at least one pause (meditation or breath)',
  only_alerts: 'generated a heart-rate alert',
};

const EXPORT_COLUMNS = [
  { key: 'user_id', label: 'User ID' },
  { key: 'name', label: 'Name' },
  { key: 'email', label: 'Email' },
  { key: 'max_run_usage_days', label: 'Max Run Usage Days' },
  { key: 'max_run_span_days', label: 'Max Run Span Days' },
  { key: 'earliest_run_start', label: 'Earliest Run Start' },
  { key: 'latest_run_end', label: 'Latest Run End' },
  { key: 'registration_date', label: 'Registered' },
  { key: 'last_active_date', label: 'Last Active' },
  { key: 'days_since_registration', label: 'Days Since Registration' },
];

const ContinuousUsageStreaks = () => {
  const navigate = useNavigate();
  // This page always shows ALL users — the global watch-segment toggle is
  // intentionally omitted here (product decision); the segment is fixed to 'all'.
  const userSegment = 'all';

  const [token, setToken] = useState('');
  const [expire, setExpire] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [rows, setRows] = useState([]);
  const [totalItem, setTotalItem] = useState(0);
  const [page, setPage] = useState(1);

  const [searchValue, setSearchValue] = useState('');
  const [gapTolerance, setGapTolerance] = useState(DEFAULT_GAP);
  const [minDays, setMinDays] = useState(DEFAULT_MIN_DAYS);
  const [mode, setMode] = useState(DEFAULT_MODE);
  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState('');

  const axiosJWT = axios.create();
  axiosJWT.interceptors.request.use(async (config) => {
    if (expire * 1000 < new Date().getTime()) {
      const res = await axios.get(`${baseUrl}/admin/api/token`);
      config.headers.Authorization = `Bearer ${res.data.accessToken}`;
      setToken(res.data.accessToken);
      setExpire(jwtDecode(res.data.accessToken).exp);
    }
    return config;
  }, (err) => Promise.reject(err));

  useEffect(() => {
    try {
      const saved = JSON.parse(sessionStorage.getItem(STATE_KEY) || 'null');
      if (saved) {
        setSearchValue(saved.searchValue || '');
        setGapTolerance(saved.gapTolerance || DEFAULT_GAP);
        setMinDays(saved.minDays || DEFAULT_MIN_DAYS);
        setMode(saved.mode || DEFAULT_MODE);
        setSortField(saved.sortField || ''); setSortOrder(saved.sortOrder || '');
        setPage(saved.page || 1);
      }
    } catch { /* ignore */ }
    refreshToken();
  }, []);

  const refreshToken = async () => {
    try {
      const res = await axios.get(`${baseUrl}/admin/api/token`);
      setToken(res.data.accessToken);
      setExpire(jwtDecode(res.data.accessToken).exp);
    } catch (err) {
      if (err.response) { navigate('/'); window.location.reload(); }
    }
  };

  useEffect(() => {
    if (token) loadList(page, searchValue, gapTolerance, minDays, sortField, sortOrder, mode);
  }, [token]);

  useEffect(() => {
    sessionStorage.setItem(STATE_KEY, JSON.stringify({
      searchValue, gapTolerance, minDays, mode, sortField, sortOrder, page,
    }));
  }, [searchValue, gapTolerance, minDays, mode, sortField, sortOrder, page]);

  // `md` (mode) is threaded explicitly alongside the other filters to avoid stale-closure
  // reads; `m` is minDays. Both list and export carry the same mode (FR-010).
  const qs = (p, s, g, m, sf, so, md) =>
    `page=${p}&limit=${LIMIT}&search=${encodeURIComponent(s)}&gap_tolerance=${g}&min_days=${m}` +
    `&sortField=${sf}&sortOrder=${so}&user_segment=${userSegment}&mode=${md}`;
  const rangeQs = (s, g, m, sf, so, md) =>
    `search=${encodeURIComponent(s)}&gap_tolerance=${g}&min_days=${m}&sortField=${sf}&sortOrder=${so}&user_segment=${userSegment}&mode=${md}`;
  const auth = () => ({ headers: { Authorization: `Bearer ${token}` } });

  const loadList = async (p, s, g, m, sf, so, md) => {
    setLoading(true); setError('');
    try {
      const res = await axiosJWT.get(`${baseUrl}/admin/api/continuous-usage-streaks?${qs(p, s, g, m, sf, so, md)}`, auth());
      setRows(res.data.data || []);
      setTotalItem(res.data.totalItem || 0);
    } catch (err) {
      setError(err?.response?.status === 401 ? 'auth' : 'load');
    } finally { setLoading(false); }
  };

  const handlePageChange = (pageNumber) => {
    setPage(pageNumber);
    loadList(pageNumber, searchValue, gapTolerance, minDays, sortField, sortOrder, mode);
  };
  const handleSearch = () => { setPage(1); loadList(1, searchValue, gapTolerance, minDays, sortField, sortOrder, mode); };
  const handleGap = (e) => {
    const g = Number(e.target.value);
    setGapTolerance(g); setPage(1);
    loadList(1, searchValue, g, minDays, sortField, sortOrder, mode);
  };
  const handleMinDays = (e) => {
    const m = Number(e.target.value);
    setMinDays(m); setPage(1);
    loadList(1, searchValue, gapTolerance, m, sortField, sortOrder, mode);
  };
  const handleMode = (e) => {
    const md = e.target.value;
    setMode(md); setPage(1);
    loadList(1, searchValue, gapTolerance, minDays, sortField, sortOrder, md);
  };
  const clearFilter = () => {
    setSearchValue(''); setGapTolerance(DEFAULT_GAP); setMinDays(DEFAULT_MIN_DAYS); setMode(DEFAULT_MODE);
    setSortField(''); setSortOrder(''); setPage(1);
    loadList(1, '', DEFAULT_GAP, DEFAULT_MIN_DAYS, '', '', DEFAULT_MODE);
  };
  const handleSort = (field) => {
    const order = sortField === field && sortOrder === 'DESC' ? 'ASC' : 'DESC';
    setSortField(field); setSortOrder(order); setPage(1);
    loadList(1, searchValue, gapTolerance, minDays, field, order, mode);
  };

  const fetchExport = async () => {
    const res = await axiosJWT.get(
      `${baseUrl}/admin/api/continuous-usage-streaks-export?${rangeQs(searchValue, gapTolerance, minDays, sortField, sortOrder, mode)}`,
      auth());
    return res.data.data || [];
  };

  const SortTh = ({ field, label }) => (
    <th className="au-th-sort" onClick={() => handleSort(field)}>
      {label} {sortField === field ? (sortOrder === 'ASC' ? '🔼' : '🔽') : ''}
    </th>
  );

  // The detail drill-down URL for one user. Carries the current gap/min so the
  // detail window matches exactly the Run Start / Run End on this row.
  const detailHref = (id) =>
    `/continuous-usage-streaks-details/${btoa(id)}?gap=${gapTolerance}&min=${minDays}&mode=${mode}`;

  const hasFilters = !!(searchValue || gapTolerance !== DEFAULT_GAP || minDays !== DEFAULT_MIN_DAYS || mode !== DEFAULT_MODE);

  return (
    <div className="panelbox">
      <Navbar />
      <div className="right-panel">
        <Header name="Pauser" />
        <div className="content-page">
          <div className="au-head">
            <span className="au-breadcrumb">Activity › <b>Continuous usage streaks</b></span>
            <span className="au-route">/continuous-usage-streaks</span>
            <span className="au-spacer" />
            <span className="au-back"><Link to="/dashboard">← Back</Link></span>
          </div>
          <h1 className="au-title">Continuous Usage Streaks</h1>

          <p className="au-range-note cus-scope-note" data-testid="cus-scope-note">
            A day counts as <b>usage</b> when the user {MODE_RULE[mode]} that day. A run breaks when
            two usage days are more than the <b>gap tolerance</b> apart; users are listed when a run
            reaches the <b>minimum days</b>.
          </p>

          {error === 'auth' ? (
            <ErrorState title="Your session expired" message="Please sign in again to continue." onRetry={() => navigate('/')} />
          ) : error === 'load' ? (
            <ErrorState message="We couldn’t load the continuous usage data. Please try again."
              onRetry={() => loadList(page, searchValue, gapTolerance, minDays, sortField, sortOrder, mode)} />
          ) : (
            <>
              <div className="cus-filters">
                <input className="au-input" type="text" placeholder="Search by name or email"
                  value={searchValue} onChange={(e) => setSearchValue(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }} aria-label="Search users" />
                <button type="button" className="au-btn au-btn--ghost" onClick={handleSearch}>Search</button>

                <label className="cus-field">
                  <span>Qualifying day</span>
                  <select className="au-select" value={mode} onChange={handleMode} aria-label="Qualifying-day mode" data-testid="cus-mode">
                    {MODE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </label>

                <label className="cus-field">
                  <span>Min consecutive days</span>
                  <select className="au-select" value={minDays} onChange={handleMinDays} aria-label="Minimum days" data-testid="cus-mindays">
                    {DAYS_OPTIONS.map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                </label>

                <label className="cus-field">
                  <span>Max gap (days)</span>
                  <select className="au-select" value={gapTolerance} onChange={handleGap} aria-label="Gap tolerance" data-testid="cus-gap">
                    {GAP_OPTIONS.map((g) => <option key={g} value={g}>{g}</option>)}
                  </select>
                </label>

                {hasFilters && (
                  <button type="button" className="au-btn au-btn--ghost" onClick={clearFilter}>Clear</button>
                )}
              </div>

              {loading ? (
                <div className="au-panel"><TableSkeleton rows={8} /></div>
              ) : rows.length === 0 ? (
                <EmptyState title="No qualifying users" hasFilters={hasFilters} onClear={clearFilter}
                  message={`No users have a run of at least ${minDays} days where they ${MODE_RULE[mode]}, with gaps no larger than ${gapTolerance} day(s).`} />
              ) : (
                <div className="au-panel">
                  <div className="au-panel__head">
                    <h4 className="au-panel__title">Users with continuous usage</h4>
                    <span className="au-spacer" />
                    <ExportButton columns={EXPORT_COLUMNS} fetchRows={fetchExport} filename="continuous-usage-streaks.csv" />
                  </div>
                  <div className="au-table-wrap">
                    <table className="au-table cus-table" data-testid="cus-table">
                      <thead>
                        <tr>
                          <th>No</th><th>Name</th><th>Email</th>
                          <SortTh field="max_run_usage_days" label="Usage Days" />
                          <SortTh field="max_run_span_days" label="Span Days" />
                          <SortTh field="earliest_run_start" label="Run Start" />
                          <SortTh field="latest_run_end" label="Run End" />
                          <SortTh field="registration_date" label="Registered" />
                          <SortTh field="last_active_date" label="Last Active" />
                          <SortTh field="days_since_registration" label="Days Since Reg." />
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((row, index) => (
                          <tr key={`${row.user_id}-${index}`}>
                            <td data-label="No">{(page - 1) * LIMIT + index + 1}</td>
                            <td data-label="Name" className="au-ellipsis">{row.name || '-'}</td>
                            <td data-label="Email" className="au-ellipsis">{row.email || '-'}</td>
                            <td data-label="Usage Days" className="tnum">
                              {/* Opens the day-by-day detail in a NEW TAB (plain anchor + target). */}
                              <a className="au-link" href={detailHref(row.user_id)}
                                target="_blank" rel="noopener noreferrer">
                                {row.max_run_usage_days}
                              </a>
                            </td>
                            <td data-label="Span Days" className="tnum">{row.max_run_span_days}</td>
                            <td data-label="Run Start">{formatUTCDate(row.earliest_run_start) || '-'}</td>
                            <td data-label="Run End">{formatUTCDate(row.latest_run_end) || '-'}</td>
                            <td data-label="Registered">{formatUTCDate(row.registration_date) || '-'}</td>
                            <td data-label="Last Active">{formatUTCDate(row.last_active_date) || '-'}</td>
                            <td data-label="Days Since Reg." className="tnum">{row.days_since_registration}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {totalItem > LIMIT && (
                    <Pagination activePage={page} itemsCountPerPage={LIMIT} totalItemsCount={totalItem}
                      pageRangeDisplayed={5} onChange={handlePageChange} />
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContinuousUsageStreaks;
